import { useCallback, useEffect, useMemo, useState } from "react";
import type { DailyStat, DrinkEntry, VolumeUnit } from "../types/models";
import { EmptyState } from "../components/EmptyState";
import { InputField, SelectField } from "../components/Field";
import { PageHeader } from "../components/PageHeader";
import { Skeleton } from "../components/Skeleton";
import { usePreferences } from "../app/PreferencesProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useToast, type ToastOptions } from "../components/Toast";
import { ApiRequestError } from "../services/api";
import { listAllDailyStats, listAllDrinkEntries } from "../services/water";
import { formatDateLabel } from "../lib/dates";
import { buildHydrationTrend, dateInputDaysAgo, earliestStatDate, mergeDailyStatsWithDrinkEntries, type HydrationTrend } from "../lib/hydrationTrend";
import { getEffectiveTimezone, getLocalDateInTimezone } from "../lib/timezone";
import { formatVolume } from "../lib/units";

type TrendRange = "week" | "month" | "sixMonths" | "year" | "all" | "custom";

export function AnalyticsPage() {
  const { t, locale, translateError } = useI18n();
  const { preferences } = usePreferences();
  const { showToast } = useToast();
  const timezone = getEffectiveTimezone(preferences.timezoneMode, preferences.fixedTimezone);
  const today = getLocalDateInTimezone(new Date(), timezone);
  const [trendRange, setTrendRange] = useState<TrendRange>("month");
  const [customStart, setCustomStart] = useState(dateInputDaysAgo(today, 29));
  const [customEnd, setCustomEnd] = useState(today);
  const [stats, setStats] = useState<DailyStat[]>([]);
  const [entries, setEntries] = useState<DrinkEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [nextStats, nextEntries] = await Promise.all([listAllDailyStats().catch(() => []), listAllDrinkEntries()]);
      setStats(nextStats);
      setEntries(nextEntries);
    } catch (error) {
      handleError(error, translateError, showToast);
    } finally {
      setLoading(false);
    }
  }, [showToast, translateError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const trendStats = useMemo(() => mergeDailyStatsWithDrinkEntries(stats, entries, timezone), [entries, stats, timezone]);
  const trendWindow = useMemo(() => readTrendWindow(trendRange, trendStats, customStart, customEnd, today), [customEnd, customStart, today, trendRange, trendStats]);
  const hydrationTrend = useMemo(() => buildHydrationTrend(trendStats, trendWindow.startDate, trendWindow.endDate), [trendStats, trendWindow]);

  return (
    <>
      <PageHeader title={t("analytics.title")} />
      <div className="stack">
        {loading ? (
          <Skeleton rows={5} />
        ) : (
          <section className="panel stack">
            <div className="section-heading">
              <div>
                <h2>{t("analytics.trend")}</h2>
                <p>{t("analytics.trendHint")}</p>
              </div>
              <SelectField
                label={t("analytics.range")}
                value={trendRange}
                onChange={(event) => setTrendRange(event.target.value as TrendRange)}
                options={[
                  { label: t("analytics.oneMonth"), value: "month" },
                  { label: t("analytics.oneWeek"), value: "week" },
                  { label: t("analytics.sixMonths"), value: "sixMonths" },
                  { label: t("analytics.oneYear"), value: "year" },
                  { label: t("analytics.allTime"), value: "all" },
                  { label: t("analytics.custom"), value: "custom" }
                ]}
              />
            </div>
            {trendRange === "custom" ? (
              <div className="grid-two">
                <InputField label={t("analytics.start")} type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} />
                <InputField label={t("analytics.end")} type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} />
              </div>
            ) : null}
            {hydrationTrend.hasData ? (
              <HydrationTrendChart trend={hydrationTrend} volumeUnit={preferences.volumeUnit} locale={locale} showWeekdayAxis={trendRange === "week"} t={t} />
            ) : (
              <EmptyState title={t("analytics.noTrendData")} />
            )}
          </section>
        )}
      </div>
    </>
  );
}

function HydrationTrendChart({
  trend,
  volumeUnit,
  locale,
  showWeekdayAxis,
  t
}: {
  trend: HydrationTrend;
  volumeUnit: VolumeUnit;
  locale: string;
  showWeekdayAxis: boolean;
  t: (key: string, variables?: Record<string, string | number>) => string;
}) {
  const width = 640;
  const height = 268;
  const left = 62;
  const right = 14;
  const top = 18;
  const bottom = 220;
  const plotWidth = width - left - right;
  const plotHeight = bottom - top;
  const maxValue = roundUpToStep(Math.max(500, trend.averageMl, ...trend.points.map((point) => point.valueMl)), 500);
  const slotWidth = trend.points.length ? plotWidth / trend.points.length : plotWidth;
  const barWidth = Math.max(4, Math.min(18, slotWidth * 0.62));
  const firstPoint = trend.points[0];
  const lastPoint = trend.points[trend.points.length - 1];
  const averageY = valueToY(trend.averageMl, maxValue, top, plotHeight);
  const yTicks = [0, maxValue / 2, maxValue];
  const showDailyXAxis = showWeekdayAxis && trend.bucket === "day" && trend.points.length <= 8;

  return (
    <div className="trend-chart">
      <div className="trend-chart__summary">
        <span>{t("analytics.average")}</span>
        <strong>{formatVolume(trend.averageMl, volumeUnit)}</strong>
        <span>{t(`analytics.bucket.${trend.bucket}`)}</span>
      </div>
      <svg className="trend-chart__svg" role="img" viewBox={`0 0 ${width} ${height}`} aria-label={t("analytics.trend")}>
        {yTicks.map((tick) => {
          const y = valueToY(tick, maxValue, top, plotHeight);
          return (
            <g key={tick}>
              <line className="trend-chart__grid" x1={left} x2={width - right} y1={y} y2={y} />
              <text className="trend-chart__axis trend-chart__axis--y" x={left - 8} y={y + 4}>
                {formatVolume(tick, volumeUnit)}
              </text>
            </g>
          );
        })}
        {trend.points.map((point, index) => {
          const x = left + index * slotWidth + (slotWidth - barWidth) / 2;
          const y = valueToY(point.valueMl, maxValue, top, plotHeight);
          const barHeight = Math.max(0, bottom - y);
          return (
            <rect
              className="trend-chart__bar"
              height={barHeight}
              key={point.key}
              rx="4"
              width={barWidth}
              x={x}
              y={barHeight > 0 ? y : bottom}
            >
              <title>
                {formatPointRange(point.startDate, point.endDate, locale)}: {formatVolume(point.valueMl, volumeUnit)}
              </title>
            </rect>
          );
        })}
        <line className="trend-chart__average" x1={left} x2={width - right} y1={averageY} y2={averageY} />
        {showDailyXAxis
          ? trend.points.map((point, index) => (
              <text className="trend-chart__axis trend-chart__axis--day" key={`axis-${point.key}`} x={left + index * slotWidth + slotWidth / 2} y={height - 16}>
                {formatWeekdayInitial(point.startDate, locale)}
              </text>
            ))
          : (
            <>
              <text className="trend-chart__axis" x={left} y={height - 16}>
                {firstPoint ? formatDateLabel(firstPoint.startDate, locale) : ""}
              </text>
              <text className="trend-chart__axis trend-chart__axis--end" x={width - right} y={height - 16}>
                {lastPoint ? formatDateLabel(lastPoint.endDate, locale) : ""}
              </text>
            </>
            )}
      </svg>
    </div>
  );
}

function readTrendWindow(range: TrendRange, stats: DailyStat[], customStart: string, customEnd: string, today: string): { startDate: string; endDate: string } {
  if (range === "custom") {
    return compareDateInputs(customStart, customEnd) <= 0 ? { startDate: customStart, endDate: customEnd } : { startDate: customEnd, endDate: customStart };
  }
  if (range === "all") {
    return { startDate: earliestStatDate(stats) ?? dateInputDaysAgo(today, 29), endDate: today };
  }

  const daysAgoByRange: Record<Exclude<TrendRange, "all" | "custom">, number> = {
    week: 6,
    month: 29,
    sixMonths: 182,
    year: 364
  };

  return { startDate: dateInputDaysAgo(today, daysAgoByRange[range]), endDate: today };
}

function valueToY(value: number, maxValue: number, top: number, plotHeight: number): number {
  return top + plotHeight - (value / maxValue) * plotHeight;
}

function roundUpToStep(value: number, step: number): number {
  return Math.ceil(value / step) * step;
}

function formatPointRange(startDate: string, endDate: string, locale: string): string {
  const start = formatDateLabel(startDate, locale);
  const end = formatDateLabel(endDate, locale);
  return startDate === endDate ? start : `${start} - ${end}`;
}

function formatWeekdayInitial(dateInput: string, locale: string): string {
  const date = new Date(`${dateInput}T00:00:00Z`);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(date);
  return weekday.replace(/[^\p{L}]/gu, "").slice(0, 1).toUpperCase();
}

function compareDateInputs(left: string, right: string): number {
  return new Date(`${left}T00:00:00Z`).getTime() - new Date(`${right}T00:00:00Z`).getTime();
}

function handleError(error: unknown, translateError: (code: string) => string, showToast: (toast: ToastOptions) => string): void {
  if (error instanceof ApiRequestError) {
    showToast({ title: translateError(error.code), tone: "error" });
  } else {
    showToast({ title: translateError("INTERNAL_ERROR"), tone: "error" });
  }
}
